import React, { useState } from 'react';
import { 
  BotIcon, 
  CopyIcon, 
  CheckIcon, 
  CheckCircleIcon,
  ServerIcon
} from './Icons';

const ChatbotSdkPreview = () => {
  const [copied, setCopied] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'agent',
      text: 'Hello! I am your Autonomous Enterprise Assistant powered by Supreme Adventure Engine. How can I assist with your tools, knowledge base, or transactions today?',
      meta: 'CORTEX_STUDIO &bull; READY'
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const embedCode = `<!-- Embed Supreme Adventure Chatbot SDK -->
<script 
  src="https://cdn.agentexpert.in/sdk/chatbot-v3.min.js" 
  data-tenant-id="your-company-tenant-id"
  data-theme="auto"
  async>
</script>`;

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = (textToSend) => {
    const query = textToSend || inputText;
    if (!query.trim()) return;

    const userMsg = { sender: 'user', text: query };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);

    setTimeout(() => {
      let responseText = '';
      let metaInfo = '';

      if (query.toLowerCase().includes('refund')) {
        responseText = 'I queried RefundPolicy KB and confirmed transaction #TX-9021 is eligible. Initiated a Stripe refund of $89.00 and emailed the receipt.';
        metaInfo = 'RAG_AGENT (RefundPolicy) ➔ TOOL_AGENT (stripe.refund) ➔ CRITIC_AGENT (Audit: PASS)';
      } else if (query.toLowerCase().includes('jira') || query.toLowerCase().includes('ticket')) {
        responseText = 'Created Jira issue #ENG-489 in sprint 24. Attached system diagnostic logs and assigned to on-call engineer.';
        metaInfo = 'TOOL_AGENT (jira.create_ticket) ➔ CRITIC_AGENT (Schema: OK)';
      } else {
        responseText = `Processed request: "${query}". Coordinated across Planner, RAG, and Tool agents to execute with 100% verification.`;
        metaInfo = 'PLANNER_AGENT ➔ MULTI-AGENT EXECUTION ➔ CRITIC_AGENT';
      }

      setMessages(prev => [
        ...prev,
        {
          sender: 'agent',
          text: responseText,
          meta: metaInfo
        }
      ]);
      setIsTyping(false);
    }, 700);
  };

  return (
    <section id="sdk" className="py-20 md:py-28 bg-slate-100/40 dark:bg-slate-900/40 border-t border-b border-slate-200/80 dark:border-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20 font-inter">
            <ServerIcon className="w-3.5 h-3.5" />
            <span>Drop-in Integration</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight font-outfit">
            Embeddable Chatbot SDK & Widget
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-light leading-relaxed">
            Deploy the full power of Supreme Adventure directly inside your web app, customer portal, or internal dashboard in under 60 seconds.
          </p>
        </div>

        {/* 2-Column interactive showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left: Code integration box */}
          <div className="lg:col-span-5 space-y-6 text-left">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white font-outfit mb-2">
                One Script Tag. Full Multi-Agent Capabilities.
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                The lightweight client SDK connects to your dedicated tenant backend over secure WebSockets with automatic token streaming, file attachment uploads, and session persistence.
              </p>
            </div>

            <div className="rounded-2xl glass-panel p-4 border border-slate-200 dark:border-slate-800 font-mono text-xs">
              <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-200 dark:border-slate-800">
                <span className="text-[11px] font-bold text-slate-400">Embed Snippet</span>
                <button
                  onClick={copyEmbed}
                  className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="w-3 h-3 text-emerald-500" />
                      <span className="text-emerald-500 font-semibold">Copied!</span>
                    </>
                  ) : (
                    <>
                      <CopyIcon className="w-3 h-3" />
                      <span>Copy Code</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="text-slate-800 dark:text-slate-200 overflow-x-auto p-1 leading-relaxed">
                <code>{embedCode}</code>
              </pre>
            </div>

            <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Automatic dark/light theme detection</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>ThreadLocal tenant isolation & session continuity</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Real-time agent execution step indicators</span>
              </div>
            </div>
          </div>

          {/* Right: Live Interactive Chat Widget Preview */}
          <div className="lg:col-span-7">
            <div className="max-w-lg mx-auto rounded-3xl glass-panel border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col h-[460px]">
              {/* Chat Widget Header */}
              <div className="px-5 py-3.5 bg-brand-600 text-white flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                    <BotIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-xs font-bold font-outfit">Supreme Adventure Copilot</h4>
                    <p className="text-[10px] text-brand-100 font-mono">Tenant: Acme Corp (Live)</p>
                  </div>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>

              {/* Chat Messages Area */}
              <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-slate-50/50 dark:bg-slate-950/40 text-left">
                {messages.map((m, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col ${m.sender === 'user' ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed font-normal shadow-sm ${
                        m.sender === 'user'
                          ? 'bg-brand-600 text-white rounded-tr-none'
                          : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-tl-none'
                      }`}
                    >
                      {m.text}
                    </div>
                    {m.meta && (
                      <span className="text-[9px] font-mono text-slate-400 mt-1 px-1">
                        {m.meta}
                      </span>
                    )}
                  </div>
                ))}
                {isTyping && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono p-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-bounce" />
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-bounce [animation-delay:0.2s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-bounce [animation-delay:0.4s]" />
                    <span className="ml-1 text-[10px]">Planner decomposing request...</span>
                  </div>
                )}
              </div>

              {/* Sample Query Prompts */}
              <div className="px-4 py-2 border-t border-slate-200/80 dark:border-slate-800/80 bg-white/50 dark:bg-slate-900/50 flex gap-1.5 overflow-x-auto text-left">
                <button
                  onClick={() => handleSend('Cancel order #ORD-9821 and issue refund')}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-600 dark:text-slate-300 shrink-0"
                >
                  💳 Issue refund
                </button>
                <button
                  onClick={() => handleSend('Create a Jira bug ticket for login error')}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-600 dark:text-slate-300 shrink-0"
                >
                  🎯 Open Jira ticket
                </button>
              </div>

              {/* Chat Input Bar */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center gap-2"
              >
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Ask a question or request a transaction..."
                  className="flex-1 bg-slate-100 dark:bg-slate-800 border-none rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <button
                  type="submit"
                  disabled={!inputText.trim()}
                  className="px-3.5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white text-xs font-semibold active:scale-95 transition-all"
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatbotSdkPreview;
